import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { UserCog, AlertCircle } from 'lucide-react';
import { useCurrentUser } from '../../../hooks/useAuth';
import { SkeletonList } from '../../../components/ui/Skeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Tabs, TabPanel } from '../../../components/ui/Tabs';
import { Pill } from '../../../components/ui/Pill';
import { type MobLite } from '../../../components/admin/UserMobilisations';
import { api, ApiError } from '../../../lib/api';
import { UsersTabContent } from './UsersTable';
import { type AdminUser } from './roles';
import { USER_TABS, USER_TAB_LABELS, isInducteeUser, resolveTab, type UserTabId } from './tabs';

const ALLOWED = ['admin', 'super_admin'];

const TAB_EMPTY_COPY: Record<UserTabId, { title: string; description: string }> = {
  staff: {
    title: 'No back office users yet',
    description:
      'Staff accounts are created on first SSO login. Once anyone signs in, they appear here for role assignment.',
  },
  inductees: {
    title: 'No self-service accounts yet',
    description:
      'Inductees appear here after their first sign-in, once their email matches an imported attendee. Learners appear after their first sign-in on the training portal.',
  },
};

export function UsersAdmin() {
  const { data: me, isLoading: meLoading } = useCurrentUser();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [allMobs, setAllMobs] = useState<MobLite[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [params, setParams] = useSearchParams();
  const tab = resolveTab(params.get('tab'));

  useEffect(() => {
    if (!me || !me.roles.some((r) => ALLOWED.includes(r))) return;
    let cancelled = false;
    (async () => {
      try {
        const [r, m] = await Promise.all([
          api<{ users: AdminUser[] }>('/api/admin/users'),
          api<{ mobilisations: MobLite[] }>('/api/admin/users/_mobilisations'),
        ]);
        if (!cancelled) {
          setUsers(r.users);
          setAllMobs(m.mobilisations);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof ApiError ? err.message : 'load failed');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [me]);

  const byTab = useMemo(() => {
    const all = users ?? [];
    return {
      staff: all.filter((u) => !isInducteeUser(u.roles)),
      inductees: all.filter((u) => isInducteeUser(u.roles)),
    } satisfies Record<UserTabId, AdminUser[]>;
  }, [users]);

  if (meLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!me) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please{' '}
        <Link to="/login" className="underline">
          sign in
        </Link>
        .
      </main>
    );
  }
  if (!me.roles.some((r) => ALLOWED.includes(r))) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        User management is restricted to admin / super_admin.
      </main>
    );
  }

  function setTab(next: UserTabId) {
    const nextParams = new URLSearchParams(params);
    // Keep the default URL clean; replace so tab flips don't pollute
    // history and Back still leaves the page in one step.
    if (next === 'staff') nextParams.delete('tab');
    else nextParams.set('tab', next);
    setParams(nextParams, { replace: true });
    // Reset the list controls so a search / page from one tab doesn't
    // carry over and hide rows in the other.
    setQ('');
    setPage(1);
  }

  function handleQ(value: string) {
    setQ(value);
    setPage(1);
  }

  function applyUpdate(updated: AdminUser) {
    setUsers((prev) => (prev ?? []).map((u) => (u.id === updated.id ? updated : u)));
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
            <UserCog className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-accent">Admin</div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Users &amp; roles</h1>
            <p className="mt-1 text-sm text-fg-muted">
              Real users come from SSO (or dev login in development). Edit their role set inline. An
              admin cannot remove their own admin role; ask another admin to demote you if needed.
            </p>
          </div>
        </header>

        {loadError ? (
          <div className="mt-6 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{loadError}</span>
          </div>
        ) : null}

        <div className="mt-6">
          <Tabs
            label="User categories"
            tabs={USER_TABS.map((t) => ({
              id: t,
              label: USER_TAB_LABELS[t],
              badge:
                users === null ? undefined : (
                  <Pill tone={t === tab ? 'accent' : 'neutral'} size="sm">
                    {byTab[t].length}
                  </Pill>
                ),
            }))}
            active={tab}
            onSelect={(t) => setTab(t as UserTabId)}
          />
        </div>

        {USER_TABS.map((t) => (
          <TabPanel key={t} tabId={t} active={tab}>
            {users === null ? (
              <div className="mt-4 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
                <SkeletonList rows={4} height="h-16" />
              </div>
            ) : byTab[t].length === 0 ? (
              <div className="mt-4 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
                <EmptyState
                  icon={<UserCog className="h-6 w-6" />}
                  title={TAB_EMPTY_COPY[t].title}
                  description={TAB_EMPTY_COPY[t].description}
                />
              </div>
            ) : (
              <UsersTabContent
                noun={t === 'inductees' ? 'inductees' : 'staff'}
                users={byTab[t]}
                allMobs={allMobs}
                meId={me.id}
                editing={editing}
                onEdit={setEditing}
                onSaved={(updated) => {
                  applyUpdate(updated);
                  setEditing(null);
                }}
                q={q}
                onQ={handleQ}
                page={page}
                onPage={setPage}
              />
            )}
          </TabPanel>
        ))}
      </div>
    </main>
  );
}
